// App.js
import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Header from './components/Layout/Header';
import Login from './pages/Login';
import Register from './pages/Register';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import TeacherDashboard from './components/TeacherDashboard';
import AdminDashboard from './components/AdminDashboard';
import ParentConsent from './components/ParentConsent';
import PrivacyPolicy from './components/PrivacyPolicy';
import Profile from './pages/Profile';
import LoadingSpinner from './components/Common/LoadingSpinner';

// Course components
import CourseList from './components/Course/CourseList';
import CourseCreate from './components/Course/CourseCreate';
import CoursePage from './components/Course/CoursePage';

// Assignment components
import AssignmentList from './components/Assignment/AssignmentList';
import AssignmentCreate from './components/Assignment/AssignmentCreate';
import AssignmentDetail from './components/Assignment/AssignmentDetail';

// Quiz components
import QuizList from './components/Quiz/QuizList';
import QuizBuilder from './components/Quiz/QuizBuilder';
import QuizTake from './components/Quiz/QuizTake';

// Record components
import RecordGenerator from './components/Record/RecordGenerator';
import RecordList from './components/Record/RecordList';
import TemplateManager from './components/Record/TemplateManager';

// Board components
import BoardList from './components/Board/BoardList';
import BoardCreate from './components/Board/BoardCreate';
import BoardDetail from './components/Board/BoardDetail';

// Teacher Risk Alerts
import AlertsPage from './pages/teacher/AlertsPage';

// Admin Risk Tuning
import RiskTuningPage from './pages/admin/RiskTuningPage';

// 보호된 라우트 컴포넌트
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();
  const location = window.location;
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="인증 확인 중..." />
      </div>
    );
  }
  
  // 사용자가 없고 현재 경로가 로그인 페이지가 아닌 경우에만 리다이렉트
  if (!user && location.pathname !== '/login') {
    return <Navigate to="/login" replace />;
  }
  
  // 사용자가 있지만 권한이 없는 경우
  if (user && allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    // 역할에 따른 기본 대시보드로 리다이렉트
    const defaultPath = user.role === 'ADMIN' ? '/admin/dashboard' : 
                       user.role === 'TEACHER' ? '/teacher/dashboard' : '/dashboard';
    return <Navigate to={defaultPath} replace />;
  }
  
  return children;
};

// 공개 라우트 컴포넌트 (로그인/회원가입 페이지용 - 로그인한 사용자는 역할에 따른 대시보드로 리다이렉트)
const PublicRoute = ({ children, isAuthPage = false }) => {
  const { user, loading } = useAuth();
  const [redirected, setRedirected] = useState(false);

  // useMemo는 반드시 컴포넌트 최상단에서 호출
  const redirectPath = useMemo(() => {
    if (!user) return null;

    switch (user.role) {
      case 'ADMIN':
        return '/admin/dashboard';
      case 'TEACHER':
        return '/teacher/dashboard';
      default:
        return '/dashboard';
    }
  }, [user]);

  useEffect(() => {
    // 로딩이 완료되고 사용자가 있으며 아직 리디렉션하지 않은 경우에만 실행
    if (!loading && user && !redirected) {
      setRedirected(true);
    }
  }, [loading, user, redirected]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="로딩 중..." />
      </div>
    );
  }

  // 로그인/회원가입 페이지인 경우에만 로그인한 사용자를 대시보드로 리디렉트
  if (isAuthPage && user && redirected && redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

// 메인 앱 컴포넌트
const AppContent = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="인증 확인 중..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* 헤더는 로그인한 사용자에게만 표시 */}
      {user && <Header />}
      
      {/* 라우트 설정 */}
      <Routes>
        {/* 공개 라우트 */}
        <Route
          path="/login"
          element={
            <PublicRoute isAuthPage={true}>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute isAuthPage={true}>
              <Register />
            </PublicRoute>
          }
        />
        <Route 
          path="/consent" 
          element={<ParentConsent />} 
        />
        <Route 
          path="/privacy-policy" 
          element={<PrivacyPolicy />} 
        />
        
        {/* 보호된 라우트 */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute allowedRoles={['STUDENT']}>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/teacher/dashboard"
          element={
            <ProtectedRoute allowedRoles={['TEACHER', 'ADMIN']}>
              <TeacherDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/alerts"
          element={
            <ProtectedRoute allowedRoles={['TEACHER', 'ADMIN']}>
              <AlertsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/risk-tuning"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <RiskTuningPage />
            </ProtectedRoute>
          }
        />
        
        {/* 프로필 페이지 - 모든 로그인한 사용자 접근 가능 */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />

        {/* Course 라우트 - 교사와 관리자만 접근 가능 */}
        <Route
          path="/courses"
          element={
            <ProtectedRoute allowedRoles={['TEACHER', 'ADMIN']}>
              <CourseList userRole={user?.role} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses/create"
          element={
            <ProtectedRoute allowedRoles={['TEACHER', 'ADMIN']}>
              <CourseCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses/:courseId"
          element={
            <ProtectedRoute allowedRoles={['TEACHER', 'STUDENT', 'ADMIN']}>
              <CoursePage userRole={user?.role} />
            </ProtectedRoute>
          }
        />

        {/* Assignment 라우트 */}
        <Route
          path="/courses/:courseId/assignments"
          element={
            <ProtectedRoute allowedRoles={['TEACHER', 'STUDENT', 'ADMIN']}>
              <AssignmentList userRole={user?.role} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses/:courseId/assignments/create"
          element={
            <ProtectedRoute allowedRoles={['TEACHER', 'ADMIN']}>
              <AssignmentCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses/:courseId/assignments/:assignmentId"
          element={
            <ProtectedRoute allowedRoles={['TEACHER', 'STUDENT', 'ADMIN']}>
              <AssignmentDetail userRole={user?.role} />
            </ProtectedRoute>
          }
        />

        {/* Quiz 라우트 */}
        <Route
          path="/courses/:courseId/quizzes"
          element={
            <ProtectedRoute allowedRoles={['TEACHER', 'STUDENT', 'ADMIN']}>
              <QuizList userRole={user?.role} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses/:courseId/quizzes/create"
          element={
            <ProtectedRoute allowedRoles={['TEACHER', 'ADMIN']}>
              <QuizBuilder />
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses/:courseId/quizzes/:quizId"
          element={
            <ProtectedRoute allowedRoles={['TEACHER', 'STUDENT', 'ADMIN']}>
              <QuizTake userRole={user?.role} />
            </ProtectedRoute>
          }
        />

        {/* Record (세특) 라우트 - 교사만 접근 가능 */}
        <Route
          path="/records/generate"
          element={
            <ProtectedRoute allowedRoles={['TEACHER', 'ADMIN']}>
              <RecordGenerator />
            </ProtectedRoute>
          }
        />
        <Route
          path="/records/my"
          element={
            <ProtectedRoute allowedRoles={['TEACHER', 'ADMIN']}>
              <RecordList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/records/templates"
          element={
            <ProtectedRoute allowedRoles={['TEACHER', 'ADMIN']}>
              <TemplateManager />
            </ProtectedRoute>
          }
        />

        {/* Board (교사 협업 게시판) 라우트 - 교사만 접근 가능 */}
        <Route
          path="/board"
          element={
            <ProtectedRoute allowedRoles={['TEACHER', 'ADMIN']}>
              <BoardList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/board/create"
          element={
            <ProtectedRoute allowedRoles={['TEACHER', 'ADMIN']}>
              <BoardCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path="/board/:postId"
          element={
            <ProtectedRoute allowedRoles={['TEACHER', 'ADMIN']}>
              <BoardDetail />
            </ProtectedRoute>
          }
        />
        
        {/* 메인 라우트 - 랜딩 페이지 또는 역할별 대시보드 */}
        <Route
          path="/"
          element={
            <PublicRoute>
              <LandingPage />
            </PublicRoute>
          }
        />
        
        {/* 404 페이지 */}
        <Route 
          path="*" 
          element={
            <div className="min-h-screen flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                <p className="text-gray-600 mb-8">페이지를 찾을 수 없습니다.</p>
                <button
                  onClick={() => window.history.back()}
                  className="btn-primary"
                >
                  이전 페이지로 돌아가기
                </button>
              </div>
            </div>
          } 
        />
      </Routes>
    </div>
  );
};

// 최상위 App 컴포넌트
const App = () => {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
        
        {/* 전역 토스트 알림 */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#fff',
              color: '#374151',
              borderRadius: '12px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: '1px solid #e5e7eb',
              padding: '16px',
              fontSize: '14px',
              fontWeight: '500',
            },
            success: {
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
            loading: {
              iconTheme: {
                primary: '#3b82f6',
                secondary: '#fff',
              },
            },
          }}
        />
      </AuthProvider>
    </Router>
  );
};

export default App;
